/**
 * Provisioning Issue Entity Schema
 *
 * The "parked" marker a school-user carries when post-OAuth provisioning failed
 * deterministically (a Blackbaud SKY role fetch returned 403 for insufficient
 * scope). It is surfaced on the user-facing endpoints (`/v1/users/me`
 * `schoolUsers[]` and `/v1/school-users/current`) so the mobile app can show a
 * "connection issue" screen instead of the misleading "waiting for approval"
 * copy.
 *
 * The marker is the source of truth read from
 * `UsersOnSchools.metadata.provisioningFailed` (written by the backend
 * degraded-provisioning path). ONLY deterministic park reasons appear here —
 * transient role-fetch failures (network, BB 5xx, quota) never produce a marker.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-11-005-fix-bb-connect-lifecycle-hardening-plan.md (U5)
 */

import { z } from 'zod'

/**
 * The deterministic reason a school-user was parked. Closed set — only
 * `BB_SCOPE_403` (Blackbaud SKY role fetch returned 403 insufficient scope)
 * today. Transient failures are never markers.
 */
export const ProvisioningIssueReasonSchema = z.enum(['BB_SCOPE_403'])
export type ProvisioningIssueReason = z.infer<typeof ProvisioningIssueReasonSchema>

/**
 * The parked-state marker surfaced to clients. Mirrors
 * `UsersOnSchools.metadata.provisioningFailed` (minus the internal
 * `environmentId`, which is not client-relevant).
 */
export const ProvisioningIssueSchema = z.object({
  reason: ProvisioningIssueReasonSchema,
  at: z.string().describe('ISO timestamp of the most recent park transition')
})
export type ProvisioningIssue = z.infer<typeof ProvisioningIssueSchema>
